import { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { FileText, Search, ShieldCheck } from "lucide-react";
import CertificateHolders from "@/pages/CertificateHolders";
import {
  InsuranceCompaniesList,
  getTotalCount,
} from "@/components/companies/InsuranceCompaniesList";
import { useInsuranceCompanies } from "@/lib/api";
import type { InsuranceCompany } from "@shared/schema";
import { InsuranceCompanyFormDialog } from "@/components/companies/InsuranceCompanyFormDialog";

// CSR-745 T9: the Companies page is a tabbed shell with two sub-sections
// -- Insurance Companies and Certificate Holders. Tab state is local
// React state only; we don't reflect it in the URL because the prototype
// doesn't, and the Certificate Holders tab already owns its own URL
// search params.
//
// CSR-822: Insurance Companies tab has a sectioned list view
// (Parent Companies / Insurance Carriers / MGAs).
// CSR-823: the "+ Add" button and edit pencils now open the
// InsuranceCompanyFormDialog. Modal state lives here so both entry
// points share a single dialog instance.
// CSR-855: the outer wrapper is only height-constrained when the
// Certificate Holders tab is active (its table needs a fixed-height
// parent for sticky-header + internal-scroll). The Insurance Companies
// tab uses natural page scroll via the Layout's <main> overflow.

export default function Companies() {
  const { data: insuranceCompanies } = useInsuranceCompanies();
  const totalCount = getTotalCount(insuranceCompanies);

  const [activeTab, setActiveTab] = useState<"insurance" | "cert-holders">(
    "insurance",
  );
  const [companySearch, setCompanySearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingCompany, setEditingCompany] = useState<InsuranceCompany | null>(
    null,
  );

  const handleAddCompany = () => {
    setEditingCompany(null);
    setDialogMode("create");
    setDialogOpen(true);
  };

  const handleEditCompany = (company: InsuranceCompany) => {
    setEditingCompany(company);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const isCertHolders = activeTab === "cert-holders";

  return (
    <Layout>
      <div
        className="flex flex-col gap-6"
        style={isCertHolders ? { height: "calc(100vh - 7rem)" } : undefined}
      >
        <div className="shrink-0">
          <h1 className="text-2xl font-extrabold font-heading text-foreground">
            Companies
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage third parties, insurance carriers, and certificate holders.
          </p>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as "insurance" | "cert-holders")}
          className={
            isCertHolders
              ? "flex flex-col flex-1 min-h-0 gap-6"
              : "flex flex-col gap-6"
          }
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList className="self-start">
              <TabsTrigger
                value="insurance"
                className="gap-2"
                data-testid="tab-insurance-companies"
              >
                <FileText className="w-4 h-4" />
                <span>Insurance Companies ({totalCount})</span>
              </TabsTrigger>
              <TabsTrigger
                value="cert-holders"
                className="gap-2"
                data-testid="tab-certificate-holders"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Certificate Holders</span>
              </TabsTrigger>
            </TabsList>

            {!isCertHolders && (
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Search companies by name or NAIC..."
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-insurance-companies"
                />
              </div>
            )}
          </div>

          <TabsContent value="insurance" className="mt-0">
            <InsuranceCompaniesList
              search={companySearch}
              onAddCompany={handleAddCompany}
              onEditCompany={handleEditCompany}
            />
          </TabsContent>

          <TabsContent
            value="cert-holders"
            className="mt-0 flex-1 min-h-0 flex flex-col gap-4"
          >
            <CertificateHolders />
          </TabsContent>
        </Tabs>

        <InsuranceCompanyFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          mode={dialogMode}
          company={editingCompany}
        />
      </div>
    </Layout>
  );
}
